import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useUnreadCount } from "@/lib/notifications";
import { useChatUnread } from "@/lib/chat";
import { Globe, MessageCircle, Bell, Home, LayoutGrid, Plus, User, Search } from "lucide-react";

const iconBtn = "w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#E4EEE8] hover:bg-[#d6e6dc] transition-colors flex items-center justify-center text-[#1A5C3A]";

function TopNav() {
  const { user } = useAuth();
  const { t } = useLang();
  const items = [
    { id: "home", to: "/", icon: Home, label: t("nav_home"), end: true },
    { id: "categories", to: "/categories", icon: LayoutGrid, label: t("nav_categories") },
    { id: "search", to: "/search", icon: Search, label: t("search_btn") },
    { id: "add", to: user ? "/post" : "/login", icon: Plus, label: t("nav_addAd"), primary: true },
    { id: "account", to: user ? "/account" : "/login", icon: User, label: t("nav_account") },
  ];
  return (
    <nav className="border-t border-[#E7E3D8] bg-white/80" data-testid="top-nav">
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto sm:px-8 flex items-center justify-between sm:justify-start gap-0 sm:gap-3 px-2 h-14 overflow-x-auto no-scrollbar">
        {items.map(({ id, to, icon: Icon, label, end, primary }) => (
          <NavLink key={id} to={to} end={end} data-testid={`topnav-${id}`}
            className={({ isActive }) => `shrink-0 h-10 px-1.5 sm:px-5 rounded-full flex items-center gap-0.5 sm:gap-2 text-[13px] sm:text-base font-bold transition-colors ${
              primary ? "bg-[#1A5C3A] text-white hover:bg-[#144a2e]"
                : isActive ? "bg-[#E4EEE8] text-[#1A5C3A]" : "text-slate-600 hover:text-[#1A5C3A] hover:bg-[#F5F0E8]"}`}>
            <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" /> {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function Navbar() {
  const { user } = useAuth();
  const unread = useUnreadCount();
  const chatUnread = useChatUnread();
  const { toggleLang, lang } = useLang();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg">
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-8 h-16 lg:h-24 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 lg:gap-3">
          <button onClick={toggleLang} data-testid="lang-toggle" className={`${iconBtn} text-xs lg:text-sm font-bold gap-0.5`}>
            <Globe className="w-3.5 h-3.5 lg:w-4 lg:h-4" />{lang === "ar" ? "EN" : "ع"}
          </button>
          <button onClick={() => navigate(user ? "/messages" : "/login")} className={`relative ${iconBtn}`} data-testid="header-chat">
            <MessageCircle className="w-5 h-5 lg:w-6 lg:h-6" />
            {chatUnread > 0 && <span className="absolute -top-1 -end-1 bg-red-600 text-white text-[9px] lg:text-[11px] font-bold rounded-full min-w-[16px] h-4 lg:min-w-[20px] lg:h-5 px-1 flex items-center justify-center" data-testid="header-chat-count">{chatUnread > 99 ? "99+" : chatUnread}</span>}
          </button>
          <button onClick={() => navigate(user ? "/notifications" : "/login")} className={`relative ${iconBtn}`} data-testid="header-bell">
            <Bell className="w-5 h-5 lg:w-6 lg:h-6" />
            {unread > 0 && <span className="absolute -top-1 -end-1 bg-red-600 text-white text-[9px] lg:text-[11px] font-bold rounded-full min-w-[16px] h-4 lg:min-w-[20px] lg:h-5 px-1 flex items-center justify-center" data-testid="header-bell-count">{unread > 99 ? "99+" : unread}</span>}
          </button>
        </div>
        <Link to="/" className="flex items-center" data-testid="navbar-logo">
          <img src="/bazaroo-logo.png?v=4" alt="bazaroo" className="h-9 lg:h-14 w-auto object-contain" />
        </Link>
      </div>
      <TopNav />
    </header>
  );
}
